import Store from "../context";
import Fetcher from "../fetcher";
import { Amount } from "../amount/amount";
import getChargeData from "../init/chargedata";
import ErrorState from "../context/common/error";
import NoopView from "../context/payment/common/noop";
import ProcessingState from "../context/common/processing";
import BackgroundProcessingState from "../context/common/background_processing";
import { isPlainObject, getValueAtPath, isFunction, withProperties, noop } from "../utils";

import {
  DEFAULT_POST_INIT,
  DEFAULT_TIMEOUT,
  isJsonResponse,
  withSharedHeaders
} from "../fetcher/utils";

let INITIAL_CHARGE_AMOUNT;

const LIMITS_CACHE = Object.create(null);
const LIMIT_API_ENDPOINT = `${process.env.API_BASE_URL}/v3/checkout/limit`;

const $fetch = withSharedHeaders(Fetcher.one
  .limit(3)
  .init(DEFAULT_POST_INIT)
  .retry(Fetcher.RetryOnNetworkError, Fetcher.RetryOnTimeout)
  .timeout(DEFAULT_TIMEOUT));

const ERR_FETCH_LIMIT_FAILURE = (function __fetchLimitFailureError__() {
  const err = new Error('FETCH_LIMIT_FAILED');
  err.name = 'ERR_FETCH_LIMIT_FAILURE';
  return Object.freeze(err);
})();

function __fetchLimit__(executor) {
  return new Promise((resolve, reject) => (
    isFunction(executor)
      ? executor(resolve, reject)
      : resolve()
  ));
}

function __fetchChargeLimit__() {
  return getChargeData().then($chargedata => {
    const currency = $chargedata.currency;
    const customer_email = $chargedata.email;
    const payment_method = getValueAtPath(Store.getState(), 'method.current');
    let network = getValueAtPath(Store.getState(), "view.context.inputs['network'].value") ? getValueAtPath(Store.getState(), "view.context.inputs['network'].value").identifier : "VODAFONE";
    let cache_key = btoa(`${currency}::${payment_method}`);

    if (!INITIAL_CHARGE_AMOUNT) {
      INITIAL_CHARGE_AMOUNT = $chargedata.amount;
    }

    const amount = INITIAL_CHARGE_AMOUNT;

    let payload = {
      amount,
      currency,
      customer_email,
      payment_method,
      public_key: $chargedata.PBFPubKey,
      checkout_id: $chargedata.modalauditid
    };

    if (payment_method === 'mobilemoneyrwanda') {
      payload.network = 'RWF';
    }

    if (payment_method === 'mobilemoneytanzania') {
      const selectedNetwork = getValueAtPath(Store.getState(), "view.context.inputs['network'].value") || null;
      network = selectedNetwork === null ? network : selectedNetwork.identifier;
      payload['network'] = network || getValueAtPath($chargedata, 'mobilemoney_network') || null;

      cache_key = btoa(`${currency}::${payment_method}::${network}`);
    }

    if (LIMITS_CACHE[cache_key]) return Promise.resolve(LIMITS_CACHE[cache_key]);

    return $fetch(LIMIT_API_ENDPOINT, {
      body: JSON.stringify(payload)
    }).then(response => {
      if (isJsonResponse(response)) {
        return response.json()
          .then(response => {
            if (isPlainObject(response) && isPlainObject(response.data)) {
              const { within_limits, min_amount, max_amount, currency } = { ...response.data };

              Object.defineProperty(LIMITS_CACHE, cache_key, {
                value: { within_limits, min_amount, max_amount, currency }, configurable: true
              } );

              return LIMITS_CACHE[cache_key];
            }

            return Promise.reject([ ERR_FETCH_LIMIT_FAILURE, network, payment_method ]);
          });
      }

      return Promise.reject([ ERR_FETCH_LIMIT_FAILURE, network, payment_method ]);
    });
  });
}

function __getChargeLimit__(callback) {
  const $this = this;
  callback = isFunction(callback) ? callback : noop;

  __fetchLimit__((resolve, reject) => {
    const { dispatch } = Store;

    const processingFinished = () => {
      dispatch(BackgroundProcessingState.actions.updateBackgroundProcessing(false));
      dispatch(ProcessingState.actions.off());
    };

    const showLimitError = $error => {
      dispatch(ErrorState.actions.updateErrorMessage($error));
    }

    const clearError = () => dispatch(ErrorState.actions.updateErrorMessage());

    const currentPaymentMethod = getValueAtPath(Store.getState(), 'method.current');

    if (['mobilemoneytanzania'].indexOf(currentPaymentMethod) === -1) {
      dispatch(ProcessingState.actions.on());
      dispatch(BackgroundProcessingState.actions.updateBackgroundProcessing(true));
    }

    // clearError();

    __fetchChargeLimit__()
      .then(
        ({ within_limits, min_amount, max_amount, currency }) => {
          clearError();
          const currentPaymentMethod = getValueAtPath(Store.getState(), 'method.current');
          if (within_limits === true) callback()
          else if (currentPaymentMethod === 'mobilemoneytanzania') {
              const { INIT_VIEW } =  $this;

              max_amount = +max_amount;
              min_amount = +min_amount;

              if (Number.isFinite(max_amount) && INITIAL_CHARGE_AMOUNT > max_amount) {
                showLimitError(`dynamic.limit.exceed.error ${new Amount(max_amount, currency)}`);
              }

              else if (Number.isFinite(min_amount) && INITIAL_CHARGE_AMOUNT < min_amount) {
                showLimitError(`dynamic.limit.below.error ${new Amount(min_amount, currency)}`);
              }

              else if (Number.isFinite(min_amount) && Number.isFinite(max_amount)) {
                showLimitError(`dynamic.limit.range.error ${new Amount(min_amount, currency)} dynamic.limit.range.to ${new Amount(max_amount, currency)}`);
              }

              else showLimitError("limit.default.error");
              INIT_VIEW()
          }
          else {
            NoopView && NoopView.activate.call($this, () => {
              max_amount = +max_amount;
              min_amount = +min_amount;

              if (Number.isFinite(max_amount) && INITIAL_CHARGE_AMOUNT > max_amount) {
                showLimitError(`dynamic.limit.exceed.error ${new Amount(max_amount, currency)}`);
              }

              else if (Number.isFinite(min_amount) && INITIAL_CHARGE_AMOUNT < min_amount) {
                showLimitError(`dynamic.limit.below.error ${new Amount(min_amount, currency)}`);
              }

              else if (Number.isFinite(min_amount) && Number.isFinite(max_amount)) {
                showLimitError(`dynamic.limit.range.error ${new Amount(min_amount, currency)} dynamic.limit.range.to ${new Amount(max_amount, currency)}`);
              }

              else showLimitError("limit.default.error");
            });
          }
        },
        (err) => (callback(), err instanceof DOMException && err.name == "AbortError" ? void 0 : reject(err))
      ).finally(() => (processingFinished(), resolve()));
  });
}

export default __getChargeLimit__;
